import { UploadTokenItem } from '@/components/chatGPT/UploadTokenItem';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, Col, Row, Tabs, Typography, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/redux/hooks';
import {
  clearAllFiles,
  getFileKeyConfig,
  resetMessages,
  uploadAllFileKeyConfig,
  watchChatBoxMessage,
  watchFileKeyConfig,
  watchFileUploads,
  downloadTemplateChatbot
} from '@/redux/slices/chatbox.slice';
import { setMessages } from '@/redux/slices/app.slice';
import { Message } from '@/enums/message.enum';
import { handleDownload } from '@/utils/common';

const UploadKeyToken = () => {
  const UploadFields: Array<{
    label: string;
    description: string;
    fileType: TypeUpload;
    fileFormat: string[];
  }> = [
    {
      label: 'Nutrition data',
      description: 'Chọn file CSV/Excel chứa dữ liệu dinh dưỡng',
      fileType: 'NUTRITION',
      fileFormat: ['.csv', '.xls', '.xlsx', '.xlsm', '.xltx', '.xltm', '.xlsb']
    },
    {
      label: 'Learning data',
      description: 'Chọn file text chứa dữ liệu học tập',
      fileType: 'LEARNING',
      fileFormat: ['.txt']
    },
    {
      label: 'Prompt data',
      description: 'Chọn file text chứa dữ liệu prompt',
      fileType: 'PROMPT',
      fileFormat: ['.txt']
    },
    {
      label: 'QA data',
      description: 'Chọn file CSV/Excel chứa dữ liệu hỏi đáp',
      fileType: 'QA',
      fileFormat: ['.csv', '.xls', '.xlsx', '.xlsm', '.xltx', '.xltm', '.xlsb']
    },
    {
      label: 'Master data',
      description: 'Chọn file CSV/Excel chứa dữ liệu tổng hợp',
      fileType: 'MASTER',
      fileFormat: ['.csv', '.xls', '.xlsx', '.xlsm', '.xltx', '.xltm', '.xlsb']
    }
  ];

  const currentFileKeyConfig = useAppSelector(watchFileKeyConfig);
  const uploadedFiles: FileUpload = useAppSelector(watchFileUploads);
  const messageInfo = useAppSelector(watchChatBoxMessage);

  const [activeTab, setActiveTab] = useState('INPUT');
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [tempApiKeyValue, setTempApiKeyValue] = useState('');

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (currentFileKeyConfig?.OPEN_API_KEY) {
      setApiKeyValue(currentFileKeyConfig.OPEN_API_KEY);
    } else {
      setApiKeyValue('');
    }
  }, [currentFileKeyConfig]);

  useEffect(() => {
    dispatch(getFileKeyConfig());
  }, [dispatch]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      if (messageInfo.type === Message.SUCCESS) {
        dispatch(resetMessages());
      }
    }
  }, [dispatch, messageInfo]);

  const handleStartUpload = () => {
    const dataUpload = Object.entries(uploadedFiles)
      .filter(([_, file]) => file.url && file.url.trim() !== '')
      .map(([type, file]) => ({
        type: type as TypeUpload,
        url: file.url
      }));

    const payload: Array<{ TYPE: TypeUpload; VALUE: string }> = dataUpload.map(
      (file) => ({
        TYPE: file.type,
        VALUE: file.url
      })
    );

    dispatch(uploadAllFileKeyConfig({ DATA: payload })).then(() => {
      dispatch(clearAllFiles());
      dispatch(getFileKeyConfig());
    });
  };

  const handleEditApiKey = () => {
    setTempApiKeyValue(apiKeyValue);
    setIsEditingApiKey(true);
  };

  const handleSaveApiKey = () => {
    setApiKeyValue(tempApiKeyValue);
    setIsEditingApiKey(false);

    if (tempApiKeyValue.trim() === '' || tempApiKeyValue === apiKeyValue)
      return;

    dispatch(
      uploadAllFileKeyConfig({
        DATA: [{ TYPE: 'OPEN_API_KEY', VALUE: tempApiKeyValue }]
      })
    );
  };

  const handleCancelEdit = () => {
    setTempApiKeyValue('');
    setIsEditingApiKey(false);
  };

  const getDisplayDescription = (field: {
    fileType: TypeUpload;
    description: string;
  }) => {
    const fileFromAPI = currentFileKeyConfig?.FILES?.find(
      (file) => file.FILE_TYPE === field.fileType
    );
    return fileFromAPI?.FILE_NAME || field.description;
  };

  const handleDownloadTemplate = async (type: TypeUpload) => {
    const template = await dispatch(downloadTemplateChatbot(type)).unwrap();
    if (template) {
      handleDownload(template.FILE_PATH, template.FILE_NAME);
    }
  };

  const tabItems = [
    {
      key: 'INPUT',
      label: 'Nhập dữ liệu',
      children: (
        <div css={boxStyles}>
          <div css={uploadSectionStyles}>
            <Button
              css={buttonStyles}
              icon={<UploadOutlined />}
              onClick={handleStartUpload}
              disabled={
                Object.values(uploadedFiles).filter(
                  ({ url }) => url && url.trim() !== ''
                ).length === 0
              }
            >
              Bắt đầu tải lên
            </Button>

            {UploadFields.map((field) => (
              <div key={field.fileType} css={uploadItemContainerStyles}>
                <UploadTokenItem
                  lable={field.label}
                  description={getDisplayDescription(field)}
                  fileType={field.fileType}
                  fileFormat={field.fileFormat}
                />
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadTemplate(field.fileType)}
                  css={downloadButtonStyles}
                />
              </div>
            ))}

            {/* API Key Configuration */}
            <div css={apiKeyConfigStyles}>
              <div css={apiKeyLabelStyles}>Config API Key:</div>

              <div css={apiKeyContentStyles}>
                {isEditingApiKey ? (
                  <Input
                    value={tempApiKeyValue}
                    onChange={(e) => setTempApiKeyValue(e.target.value)}
                    placeholder='Nhập API key...'
                    css={apiKeyInputStyles}
                  />
                ) : (
                  <Input.Password
                    css={apiKeyDisplayStyles}
                    readOnly
                    value={
                      currentFileKeyConfig?.HAS_API_KEY
                        ? '************'
                        : 'Chưa có API key được cấu hình'
                    }
                  />
                )}
              </div>

              <div css={apiKeyButtonsStyles}>
                {isEditingApiKey ? (
                  <>
                    <Button
                      type='primary'
                      icon={<SaveOutlined />}
                      onClick={handleSaveApiKey}
                      size='small'
                    >
                      Lưu
                    </Button>
                    <Button
                      icon={<CloseOutlined />}
                      onClick={handleCancelEdit}
                      size='small'
                    >
                      Hủy
                    </Button>
                  </>
                ) : (
                  <Button
                    icon={<EditOutlined />}
                    onClick={handleEditApiKey}
                    size='small'
                  >
                    Sửa
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    },
    // {
    //   key: 'HISTORY',
    //   label: 'Lịch sử nhập dữ liệu',
    //   children: (
    //     <div css={tableStyles}>
    //       <UploadKeyTokenHistory />
    //     </div>
    //   )
    // }
  ];

  return (
    <div css={rootStyles}>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Quản lý nội dung ChatGPT
          </Typography.Title>
        </Col>
      </Row>
      <Tabs
        css={tabStyle}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={tabItems}
      />
    </div>
  );
};

export default UploadKeyToken;

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

// const tableStyles = css`
//   margin-top: 1rem;
//   background: var(--white-color);
//   border-radius: 1rem;
//   padding: 0 1rem;
//   .table-top {
//     padding: 1rem 0;
//   }

//   .ant-table-thead > tr > th {
//     background: var(--blue-dark-color) !important;
//     color: var(--white-color);
//   }

//   .ant-table-cell::before {
//     display: none;
//   }
// `;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;

const uploadSectionStyles = css`
  display: flex;
  width: 50%;
  flex-direction: column;
  gap: 1.4rem;
`;

const buttonStyles = css`
  width: 30%;
`;

const apiKeyConfigStyles = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
`;

const apiKeyLabelStyles = css`
  font-weight: bold;
  font-size: 1.4rem;
  flex: 1;
`;

const apiKeyContentStyles = css`
  flex: 3;
  min-width: 0;
`;

const apiKeyDisplayStyles = css`
  width: 100%;
  padding: 0.8rem 1rem;
  background: var(--white-color);
  border: 1px solid var(--gray-light-color);
  border-radius: 0.6rem;
  min-height: 3rem;
  word-break: break-all;
  font-family: 'Courier New', monospace;
  font-size: 1.2rem;
  color: var(--gray-dark-color);
  white-space: pre-wrap;
`;

const apiKeyInputStyles = css`
  font-family: 'Courier New', monospace;
  font-size: 1.2rem;

  .ant-input {
    font-family: 'Courier New', monospace;
  }
`;

const apiKeyButtonsStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const uploadItemContainerStyles = css`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const downloadButtonStyles = css`
  min-width: 4rem;
  flex-shrink: 0;
`;
